import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { CATEGORY_IMG } from "@/lib/assets";
import { allProgress, fmtTime } from "@/lib/listening";
import { Headphones, Search, Star, Play, Sparkles, Clock, Loader2, History, TrendingUp } from "lucide-react";

const GRAD = {
  kemetic: "from-yellow-700 to-amber-950", nuwaupian: "from-amber-600 to-yellow-950",
  noi: "from-red-800 to-red-950", moorish: "from-orange-700 to-amber-950",
  hebrew: "from-blue-900 to-indigo-950", metaphysical: "from-purple-900 to-fuchsia-950",
  panafrican: "from-green-800 to-emerald-950", esoteric: "from-neutral-700 to-stone-950",
  astro: "from-indigo-900 to-slate-950", herbal: "from-green-800 to-lime-950",
};

export function AudiobookCover({ book, className = "" }) {
  const grad = GRAD[book.category] || "from-neutral-800 to-black";
  const img = book.cover_image || CATEGORY_IMG[book.category];
  return (
    <div className={`relative bg-gradient-to-br ${grad} overflow-hidden ${className}`}>
      {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" />}
      <div className="absolute inset-0 flex items-center justify-center">
        <Headphones className="text-white/70 drop-shadow-lg" size={38} />
      </div>
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="w-11 h-11 rounded-full bg-gold/90 flex items-center justify-center"><Play className="text-black ml-0.5" fill="black" size={16} /></div>
      </div>
    </div>
  );
}

function Card({ book }) {
  return (
    <Link to={`/audiobooks/${book.id}`} className="group block" data-testid="audiobook-card">
      <AudiobookCover book={book} className="aspect-[3/4] w-full" />
      <div className="mt-3">
        <h3 className="font-serif text-lg text-bone leading-snug group-hover:text-gold transition-colors line-clamp-2">{book.title}</h3>
        <p className="text-bone/50 text-sm mt-0.5">{book.author}</p>
        <p className="text-bone/30 text-xs mt-1 flex items-center gap-2">
          <span>{book.source_label || (book.source_type === "upload" ? "Audio" : "Listen")}</span>
          {book.plays > 0 && <span className="flex items-center gap-1"><Play size={10} /> {book.plays}</span>}
        </p>
      </div>
    </Link>
  );
}

function ResumeCard({ book, progress }) {
  const pct = Math.min(100, Math.round((progress.t / progress.d) * 100));
  return (
    <Link to={`/audiobooks/${book.id}`} className="group block" data-testid="audiobook-resume-card">
      <div className="relative">
        <AudiobookCover book={book} className="aspect-[3/4] w-full" />
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/50">
          <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="mt-3">
        <h3 className="font-serif text-lg text-bone leading-snug group-hover:text-gold transition-colors line-clamp-2">{book.title}</h3>
        <p className="text-gold/70 text-xs mt-1 flex items-center gap-1"><Play size={10} /> Resume · {fmtTime(progress.t)} of {fmtTime(progress.d)}</p>
      </div>
    </Link>
  );
}

export default function Audiobooks() {
  const [books, setBooks] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("search") || "");
  const category = params.get("category") || "";
  const search = params.get("search") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (search) qs.set("search", search);
    api.get(`/audiobooks?${qs.toString()}`).then((r) => setBooks(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [category, search]);

  const setCat = (slug) => {
    const p = {}; if (slug) p.category = slug; if (search) p.search = search; setParams(p);
  };
  const submitSearch = (e) => {
    e.preventDefault();
    const p = {}; if (category) p.category = category; if (term.trim()) p.search = term.trim(); setParams(p);
  };

  const featured = books.filter((b) => b.featured);
  const mostPlayed = [...books].filter((b) => b.plays > 0).sort((a, b) => b.plays - a.plays).slice(0, 5);
  const resume = allProgress()
    .map((p) => ({ book: books.find((b) => b.id === p.id), progress: p }))
    .filter((r) => r.book)
    .slice(0, 5);
  const isFiltering = category || search;

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="audiobooks-page">
      <div className="relative border-b border-white/10 grain bg-surface overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950/40 via-onyx to-onyx" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><Headphones size={16} /> Audiobook Library</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4">Listen & Awaken</h1>
          <p className="text-bone/60 max-w-2xl text-lg mb-8">Feed your spirit through sound — Diaspora history and consciousness development rooted in African and Egyptian principles. Free classics and curated listens.</p>
          <form onSubmit={submitSearch} className="relative max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-bone/40" />
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search by title, author, narrator..."
              className="w-full bg-black/50 border border-white/15 pl-11 pr-4 py-3 text-bone focus:outline-none focus:border-gold/50 text-sm" data-testid="audiobook-search" />
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {/* Continue listening shelf */}
        {!isFiltering && resume.length > 0 && (
          <section className="mb-14" data-testid="audiobooks-continue">
            <h2 className="font-serif text-2xl text-bone mb-5 flex items-center gap-2"><History size={18} className="text-gold" /> Continue Listening</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {resume.map((r) => <ResumeCard key={r.book.id} book={r.book} progress={r.progress} />)}
            </div>
          </section>
        )}

        {/* Featured shelf */}
        {!isFiltering && featured.length > 0 && (
          <section className="mb-14" data-testid="audiobooks-featured">
            <h2 className="font-serif text-2xl text-bone mb-5 flex items-center gap-2"><Star size={18} className="text-gold" /> Featured Listens</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {featured.map((b) => <Card key={b.id} book={b} />)}
            </div>
          </section>
        )}

        {/* Most Played shelf */}
        {!isFiltering && mostPlayed.length > 0 && (
          <section className="mb-14" data-testid="audiobooks-most-played">
            <h2 className="font-serif text-2xl text-bone mb-5 flex items-center gap-2"><TrendingUp size={18} className="text-gold" /> Most Played</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {mostPlayed.map((b) => <Card key={b.id} book={b} />)}
            </div>
          </section>
        )}

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setCat("")} data-testid="ab-filter-all"
            className={`px-4 py-2 text-sm transition-colors ${!category ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>All</button>
          {cats.map((c) => (
            <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`ab-filter-${c.slug}`}
              className={`px-4 py-2 text-sm transition-colors ${category === c.slug ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>
              {c.name.split(" ")[0]}
            </button>
          ))}
        </div>

        <h2 className="font-serif text-2xl text-bone mb-5 flex items-center gap-2">
          {isFiltering ? <><Sparkles size={18} className="text-gold" /> Results ({books.length})</> : <><Clock size={18} className="text-gold" /> Recently Added</>}
        </h2>
        {loading ? (
          <div className="text-bone/50 flex items-center gap-2 py-16 justify-center"><Loader2 size={18} className="animate-spin" /> Loading library...</div>
        ) : books.length === 0 ? (
          <p className="text-bone/50 py-16 text-center" data-testid="audiobooks-empty">No audiobooks found.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6" data-testid="audiobooks-grid">
            {books.map((b) => <Card key={b.id} book={b} />)}
          </div>
        )}
      </div>
    </main>
  );
}
